import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RunnerCallSheet, RunnerStep } from '../../../models/test-run.model';
import { formatFilterExpression } from '../../../shared/data-sheet-filters.codec';
import { conditionsText } from '../condition-text';

export interface CallDecision {
  execute: boolean;
  rows?: number[];
}

/** Spiegel von `GraphTraversalLimits.MAX_DEPTH`; nur fuer den Hinweis, pruefen tut der Server. */
export const LOOP_ROUND_LIMIT = 32;

type Variant = 'loop' | 'rows' | 'condition';

let nextId = 0;

/**
 * Die Frage an einem offenen Modulaufruf im manuellen Lauf (NEX-2280): Bedingung, Zeilen-Auswahl
 * mit `{…}` oder eine weitere Runde einer Schleife. Die Komponente fragt nur; senden und einfuegen
 * tut der Runner, damit Version, Fehler und Sprung an einer Stelle liegen.
 */
@Component({
  selector: 'app-call-decision',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCheckboxModule, MatProgressSpinnerModule, TranslatePipe],
  templateUrl: './call-decision.component.html',
  styleUrl: './call-decision.component.scss',
})
export class CallDecisionComponent {
  private readonly translate = inject(TranslateService);

  readonly call = input.required<RunnerStep>();
  readonly sheet = input<RunnerCallSheet | null>(null);
  /** Die Runde des Eltern-Knotens, also die Zahl der bisherigen Runden. */
  readonly rounds = input<number | null>(null);
  readonly busy = input(false);
  readonly disabled = input(false);
  readonly decided = output<CallDecision>();

  /** Die Kennung der Frage; der Runner benennt sein Detail damit. */
  readonly questionId = input(`call-decision-question-${nextId++}`);
  readonly loopMax = LOOP_ROUND_LIMIT;

  /** Die Saetze entstehen ueber `instant`; ein Sprachwechsel muss sie neu bilden. */
  private readonly language = toSignal(this.translate.onLangChange, { initialValue: null });

  readonly moduleName = computed(() => this.call().calledModuleName ?? this.call().description ?? '');
  readonly conditionText = computed(() => {
    this.language();
    return conditionsText(this.call().openCall?.conditions, this.translate);
  });

  private readonly hasRowPlaceholder = computed(() =>
    (this.call().openCall?.dataSheetFilters ?? []).some((filter) =>
      Object.values(filter ?? {}).some((value) => /\{[^{}]+\}/.test(value ?? '')),
    ),
  );

  readonly variant = computed<Variant>(() => {
    const open = this.call().openCall;
    if (open?.loopCall) return 'loop';
    if (open?.dataSheetId && this.hasRowPlaceholder() && this.sheet()) return 'rows';
    return 'condition';
  });

  /** Mit Bedingung kommt erst die Bedingung, nach „Ja" die Zeilen. */
  readonly conditionConfirmed = linkedSignal<string, boolean>({
    source: () => this.call().id,
    computation: () => false,
  });

  readonly showRows = computed(
    () => this.variant() === 'rows' && (this.conditionConfirmed() || !this.call().openCall?.conditions?.length),
  );

  readonly selectedRows = linkedSignal<string, ReadonlySet<number>>({
    source: () => this.call().id,
    computation: () => new Set<number>(),
  });

  readonly rowsExpression = computed(() => {
    const expression = formatFilterExpression(this.call().openCall?.dataSheetFilters);
    const name = this.sheet()?.name;
    return name ? `${name}|${expression}` : expression;
  });

  readonly locked = computed(() => this.busy() || this.disabled());

  readonly loopHint = computed(() => {
    this.language();
    const rounds = this.rounds() ?? 0;
    const condition = this.conditionText();
    const parts = [
      rounds === 1
        ? this.translate.instant('testRunner.callDecision.loopRoundsOne')
        : this.translate.instant('testRunner.callDecision.loopRoundsOther', { count: rounds }),
      condition ? this.translate.instant('testRunner.callDecision.conditionLabel', { text: condition }) : '',
      this.translate.instant('testRunner.callDecision.loopMax', { max: this.loopMax }),
    ];
    return parts.filter(Boolean).join(' · ');
  });

  readonly sheetRows = computed(() => {
    const sheet = this.sheet();
    if (!sheet) return [];
    return sheet.rows.map((row, index) => ({ row, nr: index + 1, active: sheet.active?.[index] !== false }));
  });

  answer(execute: boolean): void {
    if (this.locked()) return;
    if (execute && this.variant() === 'rows' && !this.showRows()) {
      this.conditionConfirmed.set(true);
      return;
    }
    this.decided.emit({ execute });
  }

  toggleRow(nr: number, checked: boolean): void {
    const next = new Set(this.selectedRows());
    if (checked) next.add(nr);
    else next.delete(nr);
    this.selectedRows.set(next);
  }

  applyRows(): void {
    if (this.locked() || this.selectedRows().size === 0) return;
    this.decided.emit({ execute: true, rows: [...this.selectedRows()].sort((a, b) => a - b) });
  }

  skipRows(): void {
    if (this.locked()) return;
    this.decided.emit({ execute: true, rows: [] });
  }
}
